import { useFeatureAccess } from "../../access-control/hooks/useFeatureAccess";
import { FEATURE_KEYS } from "../../access-control/models/role-feature.models";
import { OtHullCleaningChat } from "../../jarvis/components/OtHullCleaningChat";
import { AssistantChatConfig } from "../../jarvis/models/uwhc.models";
import { normalizeKnowledgeRepositoryApi } from "../../jarvis/utils/uwhc.utils";
import { themeAtom } from "../../shared/atoms";
import { BookOpenIcon } from "@heroicons/react/24/outline";
import { useAtomValue } from "jotai";
import { useState } from "react";
import { useParams } from "react-router-dom";
import { twMerge } from "tailwind-merge";

const OPS_KNOWLEDGE_ASSISTANT_NAME = "Ops Knowledge Assistant";
const OPS_KNOWLEDGE_CONTEXT_PROMPT =
  "I am invoking you from OpsLink. provide me a summary of relevant knowledge articles for the following content:";

function formatPortName(portName?: string | null) {
  const normalizedPortName = portName?.trim();
  if (!normalizedPortName) {
    return "your next port";
  }

  return normalizedPortName
    .toLowerCase()
    .split(/\s+/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

function formatContextValue(value?: string | number | null) {
  if (value === undefined || value === null) {
    return "";
  }

  return String(value).trim();
}

function buildOpsKnowledgeContext({
  tcCode,
  voyageNo,
  vesselName,
  nextOpenPort,
  currentCommodity,
}: {
  tcCode?: string | null;
  voyageNo?: string | number | null;
  vesselName?: string | null;
  nextOpenPort?: string | null;
  currentCommodity?: string | null;
}) {
  const contextRows = [
    { label: "TC code", value: formatContextValue(tcCode) },
    { label: "Voyage number", value: formatContextValue(voyageNo) },
    { label: "Vessel", value: formatContextValue(vesselName) },
    { label: "Next open port", value: formatContextValue(nextOpenPort) },
    { label: "Current commodity", value: formatContextValue(currentCommodity) },
  ].filter((row) => row.value.length > 0);

  if (contextRows.length === 0) {
    return "";
  }

  return `${OPS_KNOWLEDGE_CONTEXT_PROMPT}\n${contextRows.map((row) => `- ${row.label}: ${row.value}`).join("\n")}`;
}

type OpsKnowledgeAssistantProps = {
  className?: string;
  chatClassName?: string;
  portName?: string | null;
  vesselName?: string | null;
  currentCommodity?: string | null;
  initiallyOpen?: boolean;
};

export function OpsKnowledgeAssistant({
  className,
  chatClassName,
  portName,
  vesselName,
  currentCommodity,
  initiallyOpen = false,
}: Readonly<OpsKnowledgeAssistantProps>) {
  const theme = useAtomValue(themeAtom);
  const opsAssistantTheme = theme.opsKnowledgeAssistant;
  const chatUi = opsAssistantTheme.chatUi;
  const uwhcChatUi = theme.uwhcAssistant.chatUi;
  const [isOpen, setIsOpen] = useState(initiallyOpen);
  const { tcCode, voyageNo } = useParams();
  const { hasFeature } = useFeatureAccess();

  if (!hasFeature(FEATURE_KEYS.JARVIS_OPS_KNOWLEDGE_OUTPUT_AI_AGENT)) {
    return null;
  }

  const hasResolvedPortName = Boolean(portName?.trim());
  const resolvedPortName = formatPortName(portName);
  const currentCommodityLabel = currentCommodity?.trim();
  const contextParts = [
    `Voyage #${voyageNo ?? "-"}`,
    hasResolvedPortName ? resolvedPortName : undefined,
    currentCommodityLabel || undefined,
  ].filter((value): value is string => Boolean(value));
  const contextLabel = contextParts.length > 1 ? `Context: ${contextParts.join(" -> ")}` : undefined;
  const initialUserMessageContext = buildOpsKnowledgeContext({
    tcCode,
    voyageNo,
    vesselName,
    nextOpenPort: hasResolvedPortName ? resolvedPortName : null,
    currentCommodity: currentCommodityLabel,
  });
  const assistantConfig: AssistantChatConfig = {
    title: OPS_KNOWLEDGE_ASSISTANT_NAME,
    subtitle: "",
    badgeLabel: "",
    assistantLabel: OPS_KNOWLEDGE_ASSISTANT_NAME,
    contextLabel,
    systemWelcome: `${OPS_KNOWLEDGE_ASSISTANT_NAME} activated. I can help you find knowledge repository guidance, SOPs, checklists, and onboarding materials relevant to your voyage to ${resolvedPortName}.`,
    helperText:
      "AI-generated responses sourced from active ops knowledge repository items and onboarding materials. Verify critical operational decisions against current procedures.",
    quickPrompts: [
      "Summarize relevant ops knowledge for this voyage",
      `What is the pre-arrival checklist for ${resolvedPortName}?`,
      `What terminal procedures should I follow at ${resolvedPortName}?`,
    ],
    showConnectionStatus: true,
    showResponseCount: true,
    closeButtonLabel: "Collapse assistant",
    renderAssistantIcon: ({ className: iconClassName, color }) => (
      <BookOpenIcon
        className={iconClassName?.includes("h-4 w-4") ? "h-[14px] w-[14px]" : iconClassName}
        style={color ? { color } : undefined}
      />
    ),
    uiOverrides: {
      ...chatUi,
      systemAvatarBg: uwhcChatUi.systemAvatarBg,
      systemAvatarColor: uwhcChatUi.systemAvatarColor,
      systemBubble: uwhcChatUi.systemBubble,
    },
  };

  if (isOpen) {
    return (
      <div className={twMerge("h-[675px] min-h-[675px] overflow-hidden rounded-lg", className)}>
        <OtHullCleaningChat
          key={`${tcCode ?? ""}:${voyageNo ?? ""}`}
          assistantConfig={assistantConfig}
          className={twMerge("h-full rounded-lg shadow-lg", chatClassName)}
          mainClassName="[&::-webkit-scrollbar]:w-1.5"
          mainTrackClassName={chatUi.mainScrollbarTrack}
          onClose={() => setIsOpen(false)}
          placeholder="Ask about SOPs, checklists, procedures, onboarding..."
          workflow="knowledge-repository"
          responseNormalizer={normalizeKnowledgeRepositoryApi}
          initialUserMessageContext={initialUserMessageContext}
        />
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={() => setIsOpen(true)}
      className={twMerge(
        "w-full overflow-hidden rounded-lg border text-left focus:outline-none",
        chatUi.panelBorder,
        chatUi.panelBg,
        className,
      )}
      aria-expanded={isOpen}
      aria-label={`Open ${OPS_KNOWLEDGE_ASSISTANT_NAME}`}
    >
      <div
        className={twMerge(
          "flex items-start justify-between gap-4 border-b px-4 py-4",
          chatUi.panelBorder,
          chatUi.headerBg,
        )}
      >
        <div className="flex min-w-0 items-center gap-3">
          <div
            className={twMerge(
              "flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full",
              chatUi.brandIconBg,
              chatUi.brandIconShadow,
            )}
          >
            <BookOpenIcon className="h-5 w-5" style={{ color: chatUi.assistantAvatarColor }} />
          </div>

          <div className="min-w-0">
            <p className={twMerge("text-sm font-semibold", chatUi.titleText)}>{OPS_KNOWLEDGE_ASSISTANT_NAME}</p>
            <p className={twMerge("mt-1 text-xs", chatUi.subtitleText)}>SOPs, checklists &amp; onboarding materials</p>
          </div>
        </div>

        <span
          className={twMerge(
            "rounded-full border px-2.5 py-1 text-[11px] font-semibold",
            chatUi.brandTag,
            chatUi.panelBorder,
          )}
        >
          Context Ready
        </span>
      </div>

      <div className="px-4 pb-4 pt-3">
        <p className={twMerge("text-sm leading-6", opsAssistantTheme.text)}>
          Click to activate and search the knowledge repository for SOPs, checklists, terminal guidance, and onboarding
          materials.
        </p>
      </div>
    </button>
  );
}

export default OpsKnowledgeAssistant;
